/**
 * Monetizzazione Premium — hotel, B&B e negozi Plus (Profilo Base vs Plus Premium).
 * Merge con `strutture_ospitalita` e flag `isPremium` in `rete_commerciale` (JSON / Supabase-ready).
 */

import { GOVERNANCE_PRIMARY_SLUGS } from "./governanceSlugs";
import { enrichCertificazioneOspitalita } from "../utils/ratingFinale";
import { applyAdminOspitalitaOverlays } from "./certificatoAdminData";

export const PREMIUM_SLUGS = [
  "campli",
  "mosciano-sant-angelo",
  "castelli",
  "castiglione-messer-raimondo",
];

function offsetCoords(base, dLat, dLng) {
  return { lat: +(base.lat + dLat).toFixed(4), lng: +(base.lng + dLng).toFixed(4) };
}

/** @param {boolean} isPremium @param {object} [premiumData] @param {object} [certificazioneOspitalita] @param {number} [idx] */
function hotel(nome, tipo, base, camere, link, isPremium, premiumData, certificazioneOspitalita, idx = 0) {
  const dLat = idx % 2 === 0 ? 0.002 : -0.0015;
  const dLng = idx % 3 === 0 ? 0.001 : -0.001;
  return {
    nome,
    tipo,
    coords: offsetCoords(base, dLat, dLng),
    camereDisponibili: camere,
    linkPrenotazione: link,
    isPremium,
    ...(premiumData ? { premiumData } : {}),
    ...(certificazioneOspitalita ? { certificazioneOspitalita } : {}),
  };
}

/** @param {object} entity @param {object} [certificazioneOspitalita] */
function negozioWithCert(entity, certificazioneOspitalita) {
  if (!certificazioneOspitalita) return entity;
  return {
    ...entity,
    certificazioneOspitalita,
    premiumData: entity.premiumData
      ? { ...entity.premiumData, certificazioneOspitalita }
      : undefined,
  };
}

const RECENSIONI_HOTEL_CAMPLI = [
  {
    id: "rev-campli-1",
    utente: "Elena M.",
    nazionalita: "DE",
    votoPersone: 9.2,
    commento: "Camere nel palazzo storico impeccabili — colazione con prodotti locali certificati.",
    dataVisita: "2025-04-12",
    verificatoViaGPS: true,
  },
  {
    id: "rev-campli-2",
    utente: "James R.",
    nazionalita: "UK",
    votoPersone: 8.8,
    commento: "Check-in digitale fluido, vista sul borgo suggestiva.",
    dataVisita: "2025-03-28",
    verificatoViaGPS: true,
  },
];

const RECENSIONI_HOTEL_MOSCIANO = [
  {
    id: "rev-mosciano-1",
    utente: "Sofia L.",
    nazionalita: "IT",
    votoPersone: 8.5,
    commento: "Ottima base per visitare l'oleificio — parcheggio comodo.",
    dataVisita: "2025-02-18",
    verificatoViaGPS: true,
  },
];

const RECENSIONI_OLEIFICIO = [
  {
    id: "rev-olio-1",
    utente: "Marco B.",
    nazionalita: "IT",
    votoPersone: 9.0,
    commento: "Degustazione EVO guidata — QR parlante molto utile in bottega.",
    dataVisita: "2025-05-01",
    verificatoViaGPS: true,
  },
  {
    id: "rev-olio-2",
    utente: "Anna K.",
    nazionalita: "PL",
    votoPersone: 8.7,
    commento: "Spedizione rapida, olio fruttato come in frantoio.",
    dataVisita: "2025-04-22",
    verificatoViaGPS: true,
  },
];

const CERT_HOTEL_CAMPLI = enrichCertificazioneOspitalita(
  {
    numeroIspezione: "6A-OSP-2025-0102",
    dataIspezione: "2025-01-15",
    votoIspettore: 9.2,
    giudizioSartoriale: "Albergo diffuso con standard di accoglienza superiore alla media del comprensorio.",
    garanziaSosta6Amico: "Sosta verificata · filiera corta per colazione e welcome kit tipici.",
  },
  RECENSIONI_HOTEL_CAMPLI
);

const CERT_HOTEL_MOSCIANO = enrichCertificazioneOspitalita(
  {
    numeroIspezione: "6A-OSP-2025-0067",
    dataIspezione: "2024-12-10",
    votoIspettore: 8.9,
    giudizioSartoriale: "Hotel collinare con servizi Plus e integrazione oleificio del territorio.",
    garanziaSosta6Amico: "Garanzia pernottamento 6Amico · partner enogastronomici entro 5 km.",
  },
  RECENSIONI_HOTEL_MOSCIANO
);

const CERT_AGRITURISMO_CASTELLI = enrichCertificazioneOspitalita(
  {
    numeroIspezione: "6A-OSP-2025-0044",
    dataIspezione: "2025-02-01",
    votoIspettore: 9.0,
    giudizioSartoriale: "Accoglienza montana con laboratorio ceramica in loco.",
    garanziaSosta6Amico: "Percorsi sentiero e bottega certificata nello stesso hub.",
  },
  []
);

const CERT_OLEIFICIO = enrichCertificazioneOspitalita(
  {
    numeroIspezione: "6A-OSP-2025-0088",
    dataIspezione: "2025-01-20",
    votoIspettore: 9.1,
    giudizioSartoriale: "Bottega enogastronomica con carrello attivo e filiera olivicola tracciata.",
    garanziaSosta6Amico: "Visita in loco verificata GPS · campionatura lotto in archivio 6Amico.",
  },
  RECENSIONI_OLEIFICIO
);

const CERT_CERAMICA_CASTELLI = enrichCertificazioneOspitalita(
  {
    numeroIspezione: "6A-OSP-2025-0031",
    dataIspezione: "2025-02-14",
    votoIspettore: 9.3,
    giudizioSartoriale: "Laboratorio aperto al pubblico con maestri ceramisti documentati.",
    garanziaSosta6Amico: "Esperienza bottega + vendita pezzi unici certificati.",
  },
  [
    {
      id: "rev-cer-1",
      utente: "Luca P.",
      nazionalita: "IT",
      votoPersone: 9.5,
      commento: "Dimostrazione al tornio — pezzo unico consegnato con certificato.",
      dataVisita: "2025-03-10",
      verificatoViaGPS: true,
    },
  ]
);

const PREMIUM_HOTEL_DETAIL = {
  recensioniPunteggio: 4.6,
  serviziInclusi: ["Wi‑Fi", "Colazione inclusa", "Parcheggio", "Check-in digitale"],
  politicaCancellazione: "Cancellazione gratuita fino a 48h prima dell'arrivo",
  interfacciaBookingAttiva: true,
};

const OLEIFICIO_PREMIUM = {
  carrelloAttivo: true,
  inventarioProdotti: [
    { id: "evo-500", nome: "EVO Monocultivar 500ml", prezzo: 14.5, foto: "/images/prodotti/olio-evo.jpg" },
    { id: "evo-1l", nome: "EVO Riserva 1L", prezzo: 26, foto: "/images/prodotti/olio-riserva.jpg" },
    { id: "conf-miele", nome: "Confettura al miele di acacia", prezzo: 6.5, foto: "/images/prodotti/confettura.jpg" },
  ],
  seoKeywords: ["olio evo", "mosciano", "aprutino", "oleificio", "fino"],
  qrCodeParlanteUrl: "/qr/oleificio-aprutino-mosciano",
};

/** Seed per comune — overlay su commerce + strutture da JSON */
export const PREMIUM_META = {
  campli: {
    struttureOspitalita: [
      hotel(
        "Albergo Diffuso Palazzo dei Priori",
        "Albergo Diffuso",
        { lat: 42.7, lng: 13.69 },
        12,
        "/richiedi-accesso?tipo=hotel&borgo=campli",
        true,
        {
          ...PREMIUM_HOTEL_DETAIL,
          recensioniPunteggio: 4.8,
          certificazioneOspitalita: CERT_HOTEL_CAMPLI,
        },
        CERT_HOTEL_CAMPLI,
        0
      ),
      hotel(
        "B&B Via del Borgo",
        "B&B",
        { lat: 42.7, lng: 13.69 },
        4,
        "/host?borgo=campli",
        false,
        undefined,
        undefined,
        1
      ),
    ],
    negoziPremium: {
      "Bottega della Ceramica Castelli": negozioWithCert(
        {
          isPremium: true,
          premiumData: {
            carrelloAttivo: true,
            inventarioProdotti: [
              { id: "piatto-campli", nome: "Piatto ceramica Campli", prezzo: 45, foto: "/images/prodotti/ceramica-piatto.jpg" },
            ],
            seoKeywords: ["ceramica", "campli", "artigianato"],
            qrCodeParlanteUrl: "/qr/ceramica-campli",
          },
        },
        null
      ),
      "La Porchetta di Campli": { isPremium: false },
    },
  },
  "mosciano-sant-angelo": {
    struttureOspitalita: [
      hotel(
        "Hotel Il Fino",
        "Hotel",
        { lat: 42.7479, lng: 13.8885 },
        18,
        "/richiedi-accesso?tipo=hotel&borgo=mosciano-sant-angelo",
        true,
        { ...PREMIUM_HOTEL_DETAIL, certificazioneOspitalita: CERT_HOTEL_MOSCIANO },
        CERT_HOTEL_MOSCIANO
      ),
      hotel(
        "B&B Colle Sant'Angelo",
        "B&B",
        { lat: 42.7479, lng: 13.8885 },
        5,
        "/host?borgo=mosciano-sant-angelo",
        false
      ),
    ],
    negoziPremium: {
      "Oleificio Aprutino": negozioWithCert(
        { isPremium: true, premiumData: { ...OLEIFICIO_PREMIUM, certificazioneOspitalita: CERT_OLEIFICIO } },
        CERT_OLEIFICIO
      ),
      "Cantina del Borgo": { isPremium: false },
    },
  },
  castelli: {
    struttureOspitalita: [
      hotel(
        "Agriturismo Ceramica e Montagna",
        "Agriturismo",
        { lat: 42.5028, lng: 13.7028 },
        8,
        "/richiedi-accesso?tipo=agriturismo&borgo=castelli",
        true,
        {
          ...PREMIUM_HOTEL_DETAIL,
          recensioniPunteggio: 4.7,
          serviziInclusi: ["Wi‑Fi", "Degustazioni", "Sentieri guidati"],
          certificazioneOspitalita: CERT_AGRITURISMO_CASTELLI,
        },
        CERT_AGRITURISMO_CASTELLI
      ),
      hotel(
        "B&B Bottega del Vento",
        "B&B",
        { lat: 42.5028, lng: 13.7028 },
        3,
        "/host?borgo=castelli",
        false
      ),
    ],
    negoziPremium: {
      "Laboratorio Ceramica d'Arte": negozioWithCert(
        {
          isPremium: true,
          premiumData: {
            carrelloAttivo: true,
            inventarioProdotti: [
              { id: "vaso-castelli", nome: "Vaso d'arte Castelli", prezzo: 120, foto: "/images/prodotti/vaso-castelli.jpg" },
            ],
            seoKeywords: ["ceramica castelli", "arte", "montagna"],
            qrCodeParlanteUrl: "/qr/ceramica-castelli",
            certificazioneOspitalita: CERT_CERAMICA_CASTELLI,
          },
        },
        CERT_CERAMICA_CASTELLI
      ),
      "Trattoria del Ceramista": { isPremium: false },
    },
  },
  "castiglione-messer-raimondo": {
    struttureOspitalita: [
      hotel(
        "Hotel Valle del Fino",
        "Hotel",
        { lat: 42.5555, lng: 13.8888 },
        14,
        "/richiedi-accesso?tipo=hotel&borgo=castiglione-messer-raimondo",
        true,
        PREMIUM_HOTEL_DETAIL
      ),
      hotel(
        "Agriturismo della Valle",
        "Agriturismo",
        { lat: 42.5555, lng: 13.8888 },
        6,
        "/host?borgo=castiglione-messer-raimondo",
        false
      ),
    ],
    negoziPremium: {
      "Frantoio della Vallata": {
        isPremium: true,
        premiumData: {
          carrelloAttivo: true,
          inventarioProdotti: [
            { id: "evo-vallata", nome: "EVO Vallata 750ml", prezzo: 18, foto: "/images/prodotti/olio-vallata.jpg" },
          ],
          seoKeywords: ["olio", "castiglione", "valle fino"],
          qrCodeParlanteUrl: "/qr/frantoio-vallata",
        },
      },
      "Agriturismo della Valle": { isPremium: false },
    },
  },
  napoli: {
    struttureOspitalita: [
      hotel(
        "Grand Hotel Oriente",
        "Hotel",
        { lat: 40.8518, lng: 14.2681 },
        72,
        "/comune/napoli#tab-dormire",
        false,
        undefined,
        undefined,
        0
      ),
      hotel(
        "L'Alloggio dei Vassalli",
        "B&B",
        { lat: 40.8518, lng: 14.2681 },
        8,
        "/comune/napoli#tab-dormire",
        false,
        undefined,
        undefined,
        1
      ),
    ],
  },
};

/** KPI aggregati profili Premium (cruscotti) */
export const PREMIUM_NETWORK_KPI = [
  { id: "strutture", label: "Strutture ricettive", value: 8 },
  { id: "premium", label: "Profili Premium attivi", value: 6 },
];

/**
 * Applica flag Premium ai negozi della rete commerciale.
 * @param {import('../components/borgo/borgoTypes').NegozioTipico[]} negozi
 * @param {Record<string, { isPremium: boolean, premiumData?: object }>} [overrides]
 */
export function applyNegoziPremium(negozi, overrides = {}) {
  return (negozi || []).map((n) => {
    const ov = overrides[n.nome];
    if (ov) {
      return {
        ...n,
        isPremium: ov.isPremium,
        ...(ov.premiumData ? { premiumData: ov.premiumData } : {}),
        ...(ov.certificazioneOspitalita ? { certificazioneOspitalita: ov.certificazioneOspitalita } : {}),
      };
    }
    if (typeof n.isPremium === "boolean") return n;
    return { ...n, isPremium: false };
  });
}

/**
 * @param {string} slug
 * @param {object} [raw]
 * @param {{ reteNegozi?: object[] }} [commerce]
 */
export function mergePremiumFields(slug, raw, commerce = {}) {
  const meta = PREMIUM_META[slug] || {};
  const fromJson = raw?.strutture_ospitalita ?? raw?.struttureOspitalita;
  const struttureOspitalita = fromJson?.length
    ? fromJson
    : meta.struttureOspitalita ?? [];

  const jsonNegozi = raw?.rete_commerciale?.rete_negozi;
  const negoziOverrides = meta.negoziPremium ?? {};
  let reteNegozi = commerce.reteNegozi ?? [];

  if (jsonNegozi?.length) {
    reteNegozi = applyNegoziPremium(jsonNegozi, negoziOverrides);
  } else {
    reteNegozi = applyNegoziPremium(reteNegozi, negoziOverrides);
  }

  const premiumCount =
    reteNegozi.filter((n) => n.isPremium).length +
    struttureOspitalita.filter((s) => s.isPremium).length;

  const withOverlays = applyAdminOspitalitaOverlays(struttureOspitalita, reteNegozi);

  return {
    struttureOspitalita: withOverlays.struttureOspitalita,
    strutture_ospitalita: withOverlays.struttureOspitalita,
    reteNegozi: withOverlays.reteNegozi,
    rete_commerciale: {
      ...(commerce.rete_commerciale || {}),
      rete_negozi: reteNegozi,
    },
    premiumStats: {
      struttureCount: struttureOspitalita.length,
      premiumCount,
    },
  };
}

/** @param {import('../components/borgo/borgoTypes').StrutturaRicettiva[]} strutture */
export function sortStrutture(strutture) {
  const tipoOrder = { Hotel: 0, "Albergo Diffuso": 1, Agriturismo: 2, "B&B": 3 };
  return [...(strutture || [])].sort(
    (a, b) => (tipoOrder[a.tipo] ?? 9) - (tipoOrder[b.tipo] ?? 9) || (b.isPremium ? 1 : 0) - (a.isPremium ? 1 : 0)
  );
}

export { GOVERNANCE_PRIMARY_SLUGS };
